import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { Snackbar } from "@core/material";
import { Select, Store } from "@ngxs/store";
import { Observable, Subject } from "rxjs";
import { InitReportFilterForm, ReportFilterFormState, ReportFilterFormStateModel, SubmitReportFilterForm } from "./report-filter-form.dialog.state";
import { AdvancedReport, ReportFilterOptions } from "@core/data/types/advanced-report";
import { Filter, Status } from "@core/data";
import { Crop, Library } from "@library";
import { LIBRARY } from "@app/advanced-report/library";
import { takeUntil } from 'rxjs/operators';

export interface ReportFilterDialogData {
    key: string;
};

@Component({
    selector: 'pv-report-filter-form.dialog',
    templateUrl: 'report-filter-form.dialog.html',
    styleUrls: ['report-filter-form.dialog.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    preserveWhitespaces: false,
    host: {
        class: 'pv-report-filter-form-dialog'
    }
})
export class ReportFilterDialog implements OnInit, OnDestroy {

    @Select(ReportFilterFormState)
    state$: Observable<ReportFilterFormStateModel>;

    @Select(ReportFilterFormState.data)
    data$: Observable<AdvancedReport>;

    @Select(ReportFilterFormState.filter)
    filter$: Observable<Filter>;

    @Select(ReportFilterFormState.licenseeOptions)
    licenseeOptions$: Observable<string[]>;

    // TODO add required validators, and add range filters
    orgKeyControl = new FormControl([], [Validators.required]);
    searchControl = new FormControl();
    scionCultivarsControl = new FormControl([]);
    sitesControl = new FormControl([]);
    birthWeekControl = new FormControl();
    birthYearControl = new FormControl();
    scionCropIdControl = new FormControl();
    seasonMonthControl = new FormControl(null);
    seasonYearControl = new FormControl([]);
    licenseeControl = new FormControl([]);
    lockControl = new FormControl(false);

    monthOptions: { value: number; label: string }[] = [
        { value: 1, label: 'January' }, { value: 2, label: 'February' },
        { value: 3, label: 'March' },   { value: 4, label: 'April' },
        { value: 5, label: 'May' },     { value: 6, label: 'June' },
        { value: 7, label: 'July' },    { value: 8, label: 'August' },
        { value: 9, label: 'September' },{ value: 10, label: 'October' },
        { value: 11, label: 'November' },{ value: 12, label: 'December' },
    ];

    queryGroup = new FormGroup({
        ownerOrgKey: this.orgKeyControl,
        search: this.searchControl,
        scionCultivarKey: this.scionCultivarsControl,
        siteKey: this.sitesControl,
        birthYear: this.birthYearControl,
        birthWeek: this.birthWeekControl,
        scionCropId: this.scionCropIdControl,
        seasonMonth: this.seasonMonthControl,
        seasonYear: this.seasonYearControl,
        licensee: this.licenseeControl,
    });

    formGroup = new FormGroup({
        ownerOrgKey: this.orgKeyControl,
        search: this.searchControl,
        scionCultivarKey: this.scionCultivarsControl,
        siteKey: this.sitesControl,
        birthYear: this.birthYearControl,
        birthWeek: this.birthWeekControl,
        scionCropId: this.scionCropIdControl,
        seasonMonth: this.seasonMonthControl,
        seasonYear: this.seasonYearControl,
        licensee: this.licenseeControl,
        lock: this.lockControl,
    });

    cropOptions: Crop[];
    yearOptions: number[] = [];
    weekOptions: number[] = [];

    private _destroy$ = new Subject();

    constructor(
        private _store: Store,
        private _snackbar: Snackbar,
        private _dialogRef: MatDialogRef<ReportFilterDialog>,
        @Inject(LIBRARY) private _library: Library,
        @Inject(MAT_DIALOG_DATA) public data: ReportFilterDialogData
    ) { }

    ngOnInit() {
        this._store.dispatch(new InitReportFilterForm(this.data.key));

        this.cropOptions = this._library.filterExcludedCrops()

        // make year options [current year - 20] to [current year + 1]
        let cYear = new Date().getFullYear();
        for (let i = (cYear - 20); i <= (cYear + 1); i++) {
            this.yearOptions.push(i);
        }
        this.yearOptions.reverse();

        // make week options 1-53
        for (let i = 1; i <= 53; i++) {
            this.weekOptions.push(i);
        }

        this.state$.subscribe(state => {
            if (state.status === Status.COMPLETE) {
                this._dialogRef.close(state.data);
            } else if (state.status !== Status.LOADING) {
                this.formGroup.enable();
                if (state.status === Status.OK && state.filter) this.reset(state.filter)
            } else {
                this.formGroup.disable();
            }
        });

        this.lockControl.valueChanges
            .pipe(takeUntil(this._destroy$))
            .subscribe(value => {
                if (value) this.queryGroup.disable();
                else this.queryGroup.enable();
            });

        this.seasonMonthControl.valueChanges
            .pipe(takeUntil(this._destroy$))
            .subscribe(value => {
                if (value) {
                    this.seasonYearControl.setValidators([Validators.required]);
                } else {
                    this.seasonYearControl.clearValidators();
                }
                this.seasonYearControl.updateValueAndValidity({ emitEvent: false });
            });

        this.seasonYearControl.valueChanges
            .pipe(takeUntil(this._destroy$))
            .subscribe(value => {
                if (value && value.length > 0) {
                    this.seasonMonthControl.setValidators([Validators.required]);
                } else {
                    this.seasonMonthControl.clearValidators();
                }
                this.seasonMonthControl.updateValueAndValidity({ emitEvent: false });
            });

    }

    reset(filter: Filter) {
        let data = {
            ownerOrgKey: null,
            search: null,
            scionCultivarKey: [],
            siteKey: [],
            scionCropId: [],
            birthWeek: [],
            birthYear: [],
            seasonMonth: null,
            seasonYear: [],
            licensee: [],
            lock: false,
        };

        filter?.queries?.forEach(query => {
            if (query.key == 'ownerOrgKey') data.ownerOrgKey = query.value;
            if (query.key == '$fuzzy') data.search = query.value;
            if (query.key == 'scionCultivarKey') data.scionCultivarKey = query.value;
            if (query.key == 'siteKey') data.siteKey = query.value;
            if (query.key == 'scionCropId') data.scionCropId = query.value;
            if (query.key == 'birthWeek') data.birthWeek = query.value;
            if (query.key == 'birthYear') data.birthYear = query.value;
            if (query.key == 'seasonMonth') data.seasonMonth = +query.value;
            if (query.key == 'seasonYear') data.seasonYear = query.value;
            if (query.key == 'scionLicensee') data.licensee = query.value;
        })

        this.formGroup.reset(data);
    }

    attempt() {
        this.formGroup.updateValueAndValidity();
        this.formGroup.markAsDirty();
        this.formGroup.markAllAsTouched();

        if (!this.formGroup.valid) return this._snackbar.error("Invalid input. Check your input and try again");

        const form = this.formGroup.getRawValue();

        // The org key must never be dropped from the filter — fail loudly instead of storing an unscoped filter
        const orgKeyMissing = !form.ownerOrgKey || (Array.isArray(form.ownerOrgKey) && form.ownerOrgKey.length === 0);
        if (orgKeyMissing) return this._snackbar.error("The report organisation could not be determined. Close the dialog and try again.");

        const data: ReportFilterOptions = {
            ownerOrgKey: form.ownerOrgKey,
            search: form.search || null,
            scionCropId: form.scionCropId || [],
            scionCultivarKey: form.scionCultivarKey.map(data => data.key) || [],
            siteKey: form.siteKey.map(data => data.key) || [],
            birthWeek: form.birthWeek,
            birthYear: form.birthYear,
            seasonMonth: form.seasonMonth || null,
            seasonYear: form.seasonYear || [],
            licensee: form.licensee || [],
        }

        let hasValue = false;

        Object.keys(data).forEach(key => {
            if (key == 'ownerOrgKey') return;
            if (hasValue) return;
            let value = data[key];

            if (Array.isArray(value) && value.length <= 0) return;
            if (value == null) return;

            hasValue = true;
        });

        if (hasValue === false) return this._snackbar.error("Please select at least one filter option.");

        this._store.dispatch(new SubmitReportFilterForm(data, form.lock));
    }

    cancel() {
        this._dialogRef.close();
    }

    ngOnDestroy() {
        this._destroy$.next();
        this._destroy$.complete();
    }
}